import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import JitsiFrame from '../components/VideoCallFrame';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { PhoneOff, VideoOff, Loader2, AlertCircle } from 'lucide-react';

/**
 * CallPage — shared `/call/:id` endpoint for 1-on-1 and group calls.
 * Fetches the Jitsi room slug and display name from the backend and
 * hands off to the JitsiFrame component. All auth / participant
 * enforcement lives on the backend.
 */
export default function CallPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [join, setJoin] = useState(null);
  const [error, setError] = useState(null);
  const [left, setLeft] = useState(false);

  useEffect(() => {
    api.post(`/video-calls/${id}/token`)
      .then((r) => setJoin(r.data))
      .catch((err) => setError(err?.response?.data?.detail || 'Unable to start call'));
  }, [id]);

  if (left) {
    return (
      <div className='min-h-[70vh] flex items-center justify-center p-6'>
        <Card className='p-8 text-center max-w-md' data-testid='call-left-card'>
          <VideoOff className='w-10 h-10 text-slate-400 mx-auto mb-3' />
          <h2 className='font-display font-bold text-xl text-slate-900 dark:text-slate-50'>Call ended</h2>
          <p className='text-sm text-slate-500 dark:text-slate-400 mt-1'>Hope it went well!</p>
          <Button className='mt-4' onClick={() => navigate(-1)}>Back</Button>
        </Card>
      </div>
    );
  }
  if (error) {
    return (
      <div className='min-h-[70vh] flex items-center justify-center p-6'>
        <Card className='p-8 max-w-md text-center' data-testid='call-error-card'>
          <AlertCircle className='w-10 h-10 text-[#C8102E] mx-auto mb-3' />
          <h2 className='font-display font-bold text-xl text-slate-900 dark:text-slate-50'>Can&rsquo;t start the call</h2>
          <p className='text-sm text-slate-600 dark:text-slate-300 mt-2'>{error}</p>
          <Button className='mt-4' variant='outline' onClick={() => navigate(-1)}>Back</Button>
        </Card>
      </div>
    );
  }
  if (!join) {
    return (
      <div className='min-h-[70vh] flex items-center justify-center p-6 text-slate-400'>
        <Loader2 className='w-5 h-5 animate-spin mr-2' /> Starting call&hellip;
      </div>
    );
  }
  return (
    <div className='p-3 sm:p-6 max-w-6xl mx-auto' data-testid='call-page'>
      <div className='flex items-center justify-between mb-3 flex-wrap gap-2'>
        <div>
          <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#C8102E]'>Live Call</div>
          <h1 className='font-display text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-50 mt-0.5'>
            {join.call?.topic || (join.call?.participant_names?.join(', ')) || 'K9os Video Call'}
          </h1>
        </div>
        <Button variant='outline' onClick={() => setLeft(true)} data-testid='call-leave-btn'>
          <PhoneOff className='w-4 h-4 mr-2 text-[#C8102E]' /> Leave
        </Button>
      </div>
      <div className='w-full aspect-video'>
        <JitsiFrame
          roomSlug={join.slug}
          displayName={join.display_name}
          subject={join.call?.topic}
          onLeave={() => setLeft(true)}
        />
      </div>
    </div>
  );
}
